<template>
  <div v-if="bids.items.length || isClosed" class="w-full mt-5">
    <div class="w-full">
      <div v-if="isClosed" class="w-full space-y-2 text-base text-primary-dark">
        <div class="text-base font-bold text-primary-dark">
          {{ $t('text-starting-bid') }}:
          {{ startingBidPrice }}
        </div>
        <div class="text-base font-bold text-primary-dark">
          {{ $t('text-bids-count') }}:
          {{ bidCount }}
        </div>
      </div>
      <template v-else>
        <h2 class="text-base text-primary-dark font-bold pb-3">
          {{ $t('text-last-auction-bids') }}
        </h2>
        <div
          v-for="(item, idx) in bids.items"
          :key="item.id"
          :class="[ 'w-full flex', auth.idCustomer === item?.customer?.id ? 'justify-end' : 'justify-start' ]"
        >
          <RightAuctionBid
            v-if="auth.idCustomer === item?.customer?.id"
            :variant="variant"
            :item="item"
            :prev-item="prevItem(idx)"
            :selected="selected"
          />
          <LeftAuctionBid
            v-else
            :variant="variant"
            :item="item"
            :prev-item="prevItem(idx)"
            :selected="selected"
          />
        </div>
        <div v-if="loading" class="w-full flex justify-center items-center h-48 bg-transparent relative">
            <Spinner :text="$t('common:text-loading')" />
        </div>
        <div v-if="hasNextPage || loadingMore" class="flex justify-center mt-8 lg:mt-12">
          <Button
              :loading="loadingMore"
              :disabled="loadingMore"
              class-name="text-sm md:text-base font-semibold h-11"
              @click="handleLoadMore"
          >
              {{ $t("text-show-all") }}
          </Button>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import * as Sentry from "@sentry/vue";
import _ from 'lodash-es';
import { API_ENDPOINTS } from "@utils/api/endpoints";
import { AUCTION_STATUS } from "@utils/constants";
import { useAuthStore } from '@stores/auth';
import usePrice from "@utils/use-price";
import Spinner from "@components/ui/loaders/spinner/spinner";
import Button from "@components/ui/button";
import LeftAuctionBid from "@components/auction/left-auction-bid";
import RightAuctionBid from "@components/auction/right-auction-bid";

const props = defineProps({
  variant: {
    type: Object,
    required: true
  }
});

const auth = useAuthStore();

const { t } = useI18n();
const { $eventBus, $toast } = useNuxtApp();
const loading = ref(false);
const loadingMore = ref(false);
const selected = ref(null);
const bids = ref({ items: [] });
const bidCount = ref(_.get(props.variant, 'auction.bidCount', _.get(props.variant, 'auction.bid_count', 0)));
const pagination = ref({ page: 1, pageCount: 1, itemsPerPage: 15, itemCount: 0 });

const addBid = (data) => {
  if (!_.find(bids.value?.items || [], { id: data.id })) {
    if (auth.idCustomer === data.customer.id) {
      data.insertedAt = null;
    }
    bids.value.items.unshift(data);
    bidCount.value += 1;
  }
}

const hasNextPage = computed(() => pagination.value.page < pagination.value.pageCount);
const isClosed = computed(() => props.variant?.auction?.status === AUCTION_STATUS.CLOSED);
const startingBid = computed(() => _.get(props.variant, 'auction.startingBid', _.get(props.variant, 'auction.starting_bid', null)));
const startingBidPrice = computed(() => startingBid.value === null ? '' : usePrice({
  amount: +startingBid.value,
  currencyCode: props.variant?.currency?.code
})?.price);

const prevItem = (idx) => idx ? bids.value.items[idx - 1] : ({ });

const reload = async (pending = loading) => {
  pending.value = true;
  try {
    const data = await $apiFetch(API_ENDPOINTS.ESHOP_PRODUCT_VARIANT_AUCTION_BID, {
      method: 'GET',
      path: { idAuction: props.variant?.auction?.id },
      params: {
        page: pagination.value.page,
        limit: pagination.value.itemsPerPage
      }
    });
    bids.value.items = data?.items || [];
    pagination.value = data?.pagination || pagination.value;
    bidCount.value = data?.bidCount ?? data?.pagination?.itemCount ?? bidCount.value;
  } catch (error) {
    if (import.meta.client) {
      $toast.error(t('error.auction-bid.load'));
    }
    Sentry.captureException(error);
  }
  pending.value = false;
}

const handleLoadMore = async () => {
  pagination.value.itemsPerPage = pagination.value.itemCount;
  await reload(loadingMore);
}

const scrollToSelected = () => nextTick(() => {
  const element = document.getElementById(`bid-${selected.value}`);
  if (element) {
    window.scrollTo({ top: (element?.offsetTop || 0) - 240, behavior: "smooth" });
  }
});

const handleLoadSelected = async () => {
  while (import.meta.client && selected.value && !_.find(bids.value?.items || [], { id: selected.value }) && (pagination.value.page < pagination.value.pageCount)) {
    await handleLoadMore();
  }
  scrollToSelected();
};

const auctionBidAdd = (data) => {
  if (data.id === props.variant?.auction?.id) {
    addBid(data.bid);
    selected.value = data.bid.id;
  }
};

onMounted(async () => {
  if (import.meta.server || navigator?.onLine) {
    await reload();
  }
  if (import.meta.client && window?.location?.hash && window?.location?.hash.startsWith('#bid-')) {
    selected.value = parseInt(window?.location?.hash.slice(5));
    await handleLoadSelected();
  }
  $eventBus.on('auction:bid:add', auctionBidAdd);
});

onUnmounted(() => {
  $eventBus.off('auction:bid:add', auctionBidAdd);
});

</script>
